import { Building2, FileText, Info, MapPin, ShieldCheck } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type {
  CustomerAddress,
  CustomerConsent,
  CustomerDetail,
  CustomerIdDocument,
  NextOfKin,
} from '@/api/types'
import { AuditTrailDrawer } from '@/components/audit-trail-drawer'
import { MoneyDisplay } from '@/components/money-display'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatDateTime, formatShortDate } from '@/lib/datetime'
import { cn } from '@/lib/utils'

import { ADDRESS_TYPE_LABELS, CONSENT_STATEMENTS } from './constants'
import { CUSTOMER_PERMISSIONS, useCustomerPermissions } from './permissions'

/**
 * The panels behind the customer detail tabs.
 *
 * One rule runs through all of them: a value the server did not send is not rendered at all,
 * while a value the server sent as empty is rendered as a dash. The two mean different things —
 * withheld from this role, against not recorded for this customer — and a screen that showed them
 * identically would teach an operator to read "no date of birth" where the truth is "not yours to
 * see".
 */

function Field({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }) {
  const empty = value === null || value === undefined || value === ''

  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className={cn('mt-0.5 text-sm break-words text-heading', mono && 'font-mono')}>
        {empty ? <span className="text-muted">—</span> : value}
      </dd>
    </div>
  )
}

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}) {
  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

function EmptyPanel({ icon, title, description }: { icon: ReactNode; title: string; description: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
      <span className="text-muted" aria-hidden>
        {icon}
      </span>
      <p className="text-sm font-medium text-heading">{title}</p>
      <p className="max-w-md text-xs text-muted">{description}</p>
    </div>
  )
}

export function OverviewPanel({ customer }: { customer: CustomerDetail }) {
  const permissions = useCustomerPermissions()

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {!permissions.canViewPii ? (
        <Alert tone="info" className="lg:col-span-2">
          <Info aria-hidden />
          <div className="space-y-1">
            <AlertTitle>Some values are masked</AlertTitle>
            <AlertDescription>
              Your role does not hold {CUSTOMER_PERMISSIONS.piiView}, so the phone and Ghana Card
              numbers arrive partly masked, and the date of birth and address coordinates are not
              sent at all. The masking is done on the server — nothing on this screen can undo it.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <Section title="Person" description="As recorded on the Ghana Card.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="First name" value={customer.firstName} />
          <Field label="Middle name" value={customer.middleName} />
          <Field label="Surname" value={customer.lastName} />
          <Field label="Gender" value={customer.gender} />
          {customer.dateOfBirth === undefined ? null : (
            <Field label="Date of birth" value={formatShortDate(customer.dateOfBirth)} />
          )}
          <Field label="Place of birth" value={customer.placeOfBirth} />
          <Field label="Nationality" value={customer.nationality} />
          <Field label="Marital status" value={customer.maritalStatus} />
        </dl>
      </Section>

      <Section title="Contact" description="The SMS target for transaction alerts.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Mobile" value={customer.phoneNumber} mono />
          <Field label="Alternate mobile" value={customer.altPhoneNumber} mono />
          <Field label="Email" value={customer.email} />
        </dl>
      </Section>

      <Section
        title="Identity anchor"
        description="The Ghana Card number is unique across the whole institution."
      >
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Ghana Card" value={customer.ghanaCardNumber} mono />
          <Field
            label="Verified"
            value={
              customer.ghanaCardVerifiedAtUtc ? (
                formatDateTime(customer.ghanaCardVerifiedAtUtc)
              ) : (
                // A sentence, not a status token: it wraps rather than pushing the panel wider
                // than a phone screen.
                <Badge tone="warning" className="whitespace-normal">
                  Not verified against the register
                </Badge>
              )
            }
          />
        </dl>
      </Section>

      <Section title="Livelihood" description="The baseline that account activity is judged against.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Occupation" value={customer.occupation} />
          <Field label="Employer or business" value={customer.employerOrBusinessName} />
          <Field
            label="Monthly income estimate"
            value={
              <MoneyDisplay
                value={customer.monthlyIncomeEstimateMinor}
                currency={customer.currencyCode}
                className="text-left"
              />
            }
          />
          <Field label="AML risk rating" value={<StatusBadge status={customer.riskRating} />} />
        </dl>
      </Section>

      <Section title="Record" description="Where this file lives and who decided on it.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Customer number" value={customer.customerNumber} mono />
          <Field label="Home branch" value={customer.branchName ?? `Branch ${customer.branchId}`} />
          <Field label="Opened" value={formatDateTime(customer.createdAtUtc)} />
          <Field label="Status" value={<StatusBadge status={customer.status} />} />
          <Field label="KYC" value={<StatusBadge status={customer.kycStatus} />} />
          <Field
            label="KYC approved"
            value={
              customer.kycApprovedAtUtc
                ? `${formatDateTime(customer.kycApprovedAtUtc)}${customer.kycApprovedBy ? ` · ${customer.kycApprovedBy}` : ''}`
                : null
            }
          />
        </dl>
      </Section>
    </div>
  )
}

export function AddressesPanel({ addresses }: { addresses: CustomerAddress[] }) {
  if (addresses.length === 0) {
    return (
      <EmptyPanel
        icon={<MapPin className="size-6" />}
        title="No address recorded"
        description="A residence address is required before this file can leave draft — it is also the address-verification evidence the CDD file rests on."
      />
    )
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {addresses.map((address) => (
        <Card key={address.id}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {address.addressType === 'Business' ? (
                <Building2 className="size-4 text-brand-text" aria-hidden />
              ) : (
                <MapPin className="size-4 text-brand-text" aria-hidden />
              )}
              {ADDRESS_TYPE_LABELS[address.addressType]}
            </CardTitle>
            {address.isCurrent ? <Badge tone="success">Current</Badge> : <Badge>Former</Badge>}
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="GhanaPost GPS" value={address.ghanaPostGps} mono />
              <Field label="Street or area" value={address.streetOrArea} />
              <Field label="Landmark" value={address.landmark} />
              <Field label="Town" value={address.town} />
              <Field label="District" value={address.district} />
              <Field label="Region" value={address.region} />
              {/*
                Omitted entirely when withheld, and shown as uncaptured when the caller may see it
                and there is nothing there. Silently dropping the second case hides a KYC gap from
                the one person looking for it.
              */}
              {address.latitude === undefined || address.longitude === undefined ? null : (
                <Field
                  label="Coordinates"
                  value={
                    address.latitude === null || address.longitude === null
                      ? 'Not captured'
                      : `${address.latitude.toFixed(5)}, ${address.longitude.toFixed(5)}`
                  }
                  mono={address.latitude !== null}
                />
              )}
            </dl>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

const isExpired = (expiryDate: string | null) =>
  expiryDate !== null && expiryDate < new Date().toISOString().slice(0, 10)

export function IdentityPanel({ documents }: { documents: CustomerIdDocument[] }) {
  if (documents.length === 0) {
    return (
      <EmptyPanel
        icon={<FileText className="size-6" />}
        title="No identity document recorded"
        description="Since July 2022 the Ghana Card is the sole permissible identification for financial services for a Ghanaian resident, and it is what this record is keyed on."
      />
    )
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {documents.map((document) => (
        <Card key={document.id}>
          <CardHeader>
            <CardTitle>{document.idType === 'GhanaCard' ? 'Ghana Card' : document.idType}</CardTitle>
            <div className="flex items-center gap-1.5">
              {document.isPrimary ? <Badge tone="brand">Primary</Badge> : null}
              {isExpired(document.expiryDate) ? <Badge tone="danger">Expired</Badge> : null}
            </div>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Number" value={document.idNumber} mono />
              <Field
                label="Issued"
                value={document.issueDate ? formatShortDate(document.issueDate) : null}
              />
              <Field
                label="Expires"
                value={document.expiryDate ? formatShortDate(document.expiryDate) : null}
              />
              <Field
                label="Scans"
                value={
                  document.frontImageId || document.backImageId
                    ? [document.frontImageId ? 'Front' : null, document.backImageId ? 'Back' : null]
                        .filter(Boolean)
                        .join(' and ')
                    : null
                }
              />
            </dl>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

export function NextOfKinPanel({ nextOfKin }: { nextOfKin: NextOfKin[] }) {
  if (nextOfKin.length === 0) {
    return (
      <EmptyPanel
        icon={<ShieldCheck className="size-6" />}
        title="No next of kin recorded"
        description="Nothing is blocked by this. It is the contact used when the customer cannot be reached, so it is worth capturing at the next visit."
      />
    )
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {nextOfKin.map((kin) => (
        <Card key={kin.id}>
          <CardHeader>
            <CardTitle>{kin.fullName}</CardTitle>
            <Badge>{kin.relationship}</Badge>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Mobile" value={kin.phoneNumber} mono />
              <Field label="Address" value={kin.address} />
            </dl>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

export function ConsentsPanel({ consents }: { consents: CustomerConsent[] }) {
  return (
    <div className="space-y-4">
      <Alert tone="info">
        <Info aria-hidden />
        <div className="space-y-1">
          <AlertTitle>What was agreed, and to which wording</AlertTitle>
          <AlertDescription>
            Each consent is stored with the version of the text the customer was shown, so what
            they agreed to can be reconstructed years later. Marketing consent is optional and may
            be withdrawn; transactional alerts about the customer&apos;s own accounts do not depend
            on it.
          </AlertDescription>
        </div>
      </Alert>

      {consents.length === 0 ? (
        <EmptyPanel
          icon={<ShieldCheck className="size-6" />}
          title="No consent recorded"
          description="Account-operation consent is the basis on which this record is held. Without it the file cannot leave draft."
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {consents.map((consent) => {
            const statement = CONSENT_STATEMENTS.find(
              (candidate) => candidate.consentType === consent.consentType,
            )

            return (
              <Card key={consent.consentType}>
                <CardHeader>
                  <div className="min-w-0">
                    <CardTitle>{statement?.title ?? consent.consentType}</CardTitle>
                    {statement ? <p className="mt-1 text-xs text-body">{statement.text}</p> : null}
                  </div>
                  <Badge tone={consent.granted ? 'success' : 'neutral'}>
                    {consent.granted ? 'Granted' : 'Withheld'}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                    <Field label="Text version" value={consent.textVersion} mono />
                    <Field label="Captured by" value={consent.capturedBy} />
                    <Field label="Recorded" value={formatDateTime(consent.grantedAtUtc)} />
                  </dl>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function AuditPanel({ customer }: { customer: CustomerDetail }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Audit trail</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="max-w-2xl text-sm text-body">
            Every change to this record, and every attempt that was refused, is held in an
            append-only trail sealed into a hash chain. Nothing on this screen can edit or remove an
            entry — the panel is a window onto evidence, not a log viewer.
          </p>
          <p className="max-w-2xl text-sm text-body">
            Reading a trail is itself an audited action, so it is fetched when you ask for it rather
            than when you open this tab.
          </p>
          <Button variant="secondary" onClick={() => setOpen(true)}>
            <FileText />
            Open the audit trail
          </Button>
        </CardContent>
      </Card>

      <AuditTrailDrawer
        open={open}
        onOpenChange={setOpen}
        entityType="Customer"
        entityId={customer.id}
        subject={customer.fullName}
      />
    </>
  )
}
